/**
 * Filters – Form section renderers
 */

import { store } from '../../state';
import {
  DISTANCE_SLIDER,
  formatHeight,
  getUnits,
  kmToSlider,
  sliderToDisplayLabel,
  wantsKidsLabel,
} from '../../utils';
import {
  renderCheckboxGroup as renderCheckboxGroupShared,
  renderRadioGroup as renderRadioGroupShared,
} from '../../components/form-groups';
import {
  FREQUENCY_OPTIONS,
  EXERCISE_OPTIONS,
  WANTS_MORE_KIDS_OPTIONS,
  KIDS_YOUNGEST_AGE_OPTIONS,
  KIDS_COUNT_OPTIONS,
  PREFERRED_STRUCTURE_OPTIONS,
  EDUCATION_LEVEL_OPTIONS,
  EMPLOYMENT_STATUS_OPTIONS,
  LANGUAGES_OPTIONS,
  ZODIAC_OPTIONS,
  BODY_TYPE_OPTIONS,
  ETHNICITY_OPTIONS,
  POLITICAL_VIEW_OPTIONS,
  INCOME_RANGE_OPTIONS,
  FLEXIBILITY_OPTIONS_UI,
  SEXUAL_ROLE_OPTIONS,
  SEXUAL_DYNAMICS_OPTIONS,
  SEXUAL_ORIENTATION_OPTIONS,
  RELIGION_OPTIONS,
  PET_TYPE_OPTIONS,
  RELATIONSHIP_INTENT_OPTIONS,
} from '../../options';
import type { Option } from '../../options';
import type { IntentEntry } from '../../options';
import type { FiltersState } from './types';
import { GENDER_FILTER_OPTIONS, RECENTLY_ACTIVE_SLIDER } from './types';
import { getHeightSliderMax, cmToHeightSlider, hoursToActivitySlider, formatActivityLabel } from './slider-utils';

/** Render a single intent entry card */
function renderIntentEntryCard(entry: IntentEntry, index: number): string {
  return `
    <div class="intent-entry-card" data-entry-index="${index}">
      <div class="intent-entry-header">
        <span class="intent-entry-number">Entry ${index + 1}</span>
        <button type="button" class="btn-icon btn-remove-intent-entry" data-entry-index="${index}" title="Remove entry">
          <i class="fas fa-times"></i>
        </button>
      </div>
      <div class="intent-entry-fields">
        <div class="intent-entry-field">
          <span class="group-label">Their Gender</span>
          <div class="checkbox-group">
            ${GENDER_FILTER_OPTIONS.map(opt => `
              <label class="checkbox-item">
                <input
                  type="checkbox"
                  name="intent_entry_genders_${index}"
                  value="${opt.value}"
                  ${entry.genders?.includes(opt.value as any) ? 'checked' : ''}
                  data-entry-index="${index}"
                  data-entry-field="genders"
                />
                ${opt.label}
              </label>
            `).join('')}
          </div>
        </div>
        <div class="intent-entry-field">
          <span class="group-label">Relationship Goals</span>
          <div class="checkbox-group">
            ${RELATIONSHIP_INTENT_OPTIONS.filter(o => o.value && o.value !== 'pnta').map(opt => `
              <label class="checkbox-item">
                <input
                  type="checkbox"
                  name="intent_entry_intents_${index}"
                  value="${opt.value}"
                  ${entry.intents?.includes(opt.value as any) ? 'checked' : ''}
                  data-entry-index="${index}"
                  data-entry-field="intents"
                />
                ${opt.label}
              </label>
            `).join('')}
          </div>
        </div>
        <div class="intent-entry-field">
          <div class="preference-row">
            <span class="group-label">Age Range</span>
            <span class="filter-value intent-entry-age-display" data-entry-index="${index}">${(entry.age_min ?? 18) === 18 && (entry.age_max ?? 99) === 99 ? 'Any' : `${entry.age_min ?? 18}-${entry.age_max ?? 99}`}</span>
          </div>
          <div class="flex gap-2">
            <input type="range" class="input intent-entry-age-min" data-entry-index="${index}" min="18" max="99" value="${entry.age_min ?? 18}" />
            <input type="range" class="input intent-entry-age-max" data-entry-index="${index}" min="18" max="99" value="${entry.age_max ?? 99}" />
          </div>
        </div>
      </div>
    </div>
  `;
}

/** Render the full intent entries section */
function renderIntentEntriesSection(entries: IntentEntry[]): string {
  const cards = entries.map((entry, i) => renderIntentEntryCard(entry, i)).join('');
  return `
    <div id="intent-entries-list">
      ${cards || '<div class="empty-intent-entries">No entries yet. Add one to specify what you\'re looking for.</div>'}
    </div>
    <button type="button" id="add-intent-entry-btn" class="btn btn-secondary btn-sm mt-2">
      <i class="fas fa-plus"></i> Add Entry
    </button>
  `;
}

// Thin wrappers over shared form-group renderers with filter-specific config
function renderCheckboxGroup(
  fieldId: string,
  options: Option[],
  selectedValues: string[],
  labelText: string
): string {
  return renderCheckboxGroupShared({
    fieldId, options, selectedValues, labelText,
    dataAttr: 'data-filter-field',
    wrapInInputGroup: true,
  });
}

function renderRadioGroup(
  fieldId: string,
  options: Option[],
  selectedValue: string | null,
  labelText: string
): string {
  return renderRadioGroupShared({
    fieldId, options, selectedValue, labelText,
    dataAttr: 'data-filter-field',
    wrapInInputGroup: true,
  });
}

export function renderFiltersForm(filters: FiltersState, isLikesMode = false): void {
  const basicTab = document.getElementById('filters-tab-basic')!;
  const advancedTab = document.getElementById('filters-tab-advanced')!;
  const activityTab = document.getElementById('filters-tab-activity')!;
  const units = getUnits();

  // Check if user has sexual fields filled (for gating Tier 5)
  const userProfile = store.getState().user?.profile;
  const showSexualFilters = userProfile?.profile_json?.sexual?.role ||
                            userProfile?.profile_json?.sexual?.dynamics;

  // === BASIC TAB ===
  basicTab.innerHTML = `
    <div class="profile-edit-form">

      <!-- 2-WAY MATCHING -->
      <div class="form-section">
        <div class="form-section-header" data-section="two-way">
          <h3><i class="fas fa-exchange-alt"></i> 2-Way Matching <span class="save-indicator" id="indicator-two-way">Saved <i class="fas fa-check"></i></span></h3>
          <i class="fas fa-chevron-down section-toggle"></i>
        </div>
        <div class="form-section-content" id="section-two-way">
          <div class="input-group">
            <label class="checkbox-item">
              <input type="checkbox" id="two-way-toggle" ${filters.two_way_match ? 'checked' : ''} />
              Only show people where my age, gender, and relationship intent match their preferences.
            </label>
          </div>
        </div>
      </div>

      <!-- INTELLIGENCE SUBSTRATE -->
      <div class="form-section">
        <div class="form-section-header" data-section="substrate">
          <h3><i class="fas fa-microchip"></i> Intelligence Substrate <span class="save-indicator" id="indicator-substrate">Saved <i class="fas fa-check"></i></span></h3>
          <i class="fas fa-chevron-down section-toggle"></i>
        </div>
        <div class="form-section-content" id="section-substrate">
          <div class="input-group">
            <div class="checkbox-group">
              <label class="checkbox-item">
                <input type="checkbox" id="substrate-human" ${filters.show_human_users ? 'checked' : ''} />
                Human&nbsp;<wbr>(carbon&#8209;based)
              </label>
              <label class="checkbox-item">
                <input type="checkbox" id="substrate-ai" ${filters.show_ai_users ? 'checked' : ''} />
                AI&nbsp;<wbr>(silicon&#8209;based)
              </label>
            </div>
          </div>
        </div>
      </div>

      <!-- FILTERS -->
      <div class="form-section">
        <div class="form-section-header" data-section="basic">
          <h3><i class="fas fa-filter"></i> Filters</h3>
          <i class="fas fa-chevron-down section-toggle"></i>
        </div>
        <div class="form-section-content" id="section-basic">

          <!-- Distance -->
          <div class="input-group">
            <div class="preference-row">
              <span class="group-label">Maximum Distance</span>
              <span class="save-indicator" id="indicator-distance">Saved <i class="fas fa-check"></i></span>
              <span id="distance-display" class="filter-value">${sliderToDisplayLabel(kmToSlider(filters.distance_max_km, units), units)}</span>
            </div>
            <input type="range" id="distance-slider" class="input"
              min="${DISTANCE_SLIDER.MIN}" max="${DISTANCE_SLIDER.MAX}"
              value="${kmToSlider(filters.distance_max_km, units)}" />
          </div>

          <!-- Last Active -->
          <div class="input-group">
            <div class="preference-row">
              <span class="group-label">Last Active</span>
              <span class="save-indicator" id="indicator-activity">Saved <i class="fas fa-check"></i></span>
              <span id="activity-display" class="filter-value">${formatActivityLabel(filters.recently_active_hours)}</span>
            </div>
            <input type="range" id="activity-slider" class="input"
              min="${RECENTLY_ACTIVE_SLIDER.MIN}" max="${RECENTLY_ACTIVE_SLIDER.MAX}"
              value="${hoursToActivitySlider(filters.recently_active_hours)}" />
          </div>

          <!-- What I'm Looking For (compound intent entries) -->
          <div class="input-group">
            <div class="label-with-indicator">
              <span class="group-label">What I'm Looking For</span>
              <span class="save-indicator" id="indicator-intent-entries">Saved <i class="fas fa-check"></i></span>
            </div>
            ${renderIntentEntriesSection(filters.intent_entries)}
          </div>

        </div>
      </div>

    </div>
  `;

  // === ADVANCED TAB ===
  // Determine display location: travel override > profile location
  const profileCity = userProfile?.city || '';
  const profileState = userProfile?.state || '';
  const profileCountry = userProfile?.country || '';
  const hasTravelOverride = filters.travel_city != null || filters.travel_state != null || filters.travel_country != null;
  const displayCity = hasTravelOverride ? (filters.travel_city || '') : profileCity;
  const displayState = hasTravelOverride ? (filters.travel_state || '') : profileState;
  const displayCountry = hasTravelOverride ? (filters.travel_country || '') : profileCountry;

  // Location feedback
  let locationFeedback = '';
  if (hasTravelOverride && filters.travel_lat != null && filters.travel_lng != null) {
    locationFeedback = `${Number(filters.travel_lat).toFixed(4)}, ${Number(filters.travel_lng).toFixed(4)}`;
  } else if (hasTravelOverride) {
    locationFeedback = 'Location not found';
  } else if (userProfile?.latitude != null && userProfile?.longitude != null) {
    locationFeedback = `${Number(userProfile.latitude).toFixed(4)}, ${Number(userProfile.longitude).toFixed(4)}`;
  } else {
    locationFeedback = 'Set your location in your profile';
  }

  advancedTab.innerHTML = `
    <div class="profile-edit-form">

      <!-- LOCATION -->
      <div class="form-section">
        <div class="form-section-header" data-section="travel-location">
          <h3><i class="fas fa-map-marker-alt"></i> Location <span class="save-indicator" id="indicator-travel-location">Saved <i class="fas fa-check"></i></span></h3>
          <i class="fas fa-chevron-down section-toggle"></i>
        </div>
        <div class="form-section-content" id="section-travel-location">
          <div class="input-group">
            <label class="group-label" for="travel-city">City</label>
            <input type="text" id="travel-city" class="input" value="${displayCity}" placeholder="City" autocomplete="off" />
          </div>
          <div class="input-group">
            <label class="group-label" for="travel-state">State / Region</label>
            <input type="text" id="travel-state" class="input" value="${displayState}" placeholder="State" autocomplete="off" />
          </div>
          <div class="input-group">
            <label class="group-label" for="travel-country">Country</label>
            <input type="text" id="travel-country" class="input" value="${displayCountry}" placeholder="Country" autocomplete="off" />
          </div>
          <div class="input-group">
            <div id="travel-location-display" class="input-hint ${hasTravelOverride && !filters.travel_lat ? 'text-error' : ''}">${locationFeedback}</div>
            <button type="button" id="travel-reset-btn" class="btn btn-secondary btn-sm mt-2" style="${hasTravelOverride ? '' : 'display:none;'}">
              <i class="fas fa-undo"></i> Reset to Profile Location
            </button>
          </div>
        </div>
      </div>

      <!-- APPEARANCE -->
      <div class="form-section">
        <div class="form-section-header" data-section="appearance">
          <h3><i class="fas fa-ruler-vertical"></i> Their Appearance</h3>
          <i class="fas fa-chevron-down section-toggle"></i>
        </div>
        <div class="form-section-content" id="section-appearance">

          <!-- Height Range -->
          <div class="input-group">
            <div class="preference-row">
              <span class="group-label">Their Height</span>
              <span class="save-indicator" id="indicator-height">Saved <i class="fas fa-check"></i></span>
              <span id="height-display" class="filter-value">${filters.height_min_cm === null ? 'No Min' : formatHeight(filters.height_min_cm, units)} - ${filters.height_max_cm === null ? 'No Max' : formatHeight(filters.height_max_cm, units)}</span>
            </div>
            <div class="flex gap-2">
              <input type="range" id="height-min-slider" class="input"
                min="0" max="${getHeightSliderMax(units) - 1}"
                value="${cmToHeightSlider(filters.height_min_cm, units, false)}" />
              <input type="range" id="height-max-slider" class="input"
                min="1" max="${getHeightSliderMax(units)}"
                value="${cmToHeightSlider(filters.height_max_cm, units, true)}" />
            </div>
          </div>

          <!-- Body Type -->
          ${renderCheckboxGroup('body_types', BODY_TYPE_OPTIONS, filters.body_types, 'Their Body Type')}

        </div>
      </div>

      <!-- KIDS -->
      <div class="form-section">
        <div class="form-section-header" data-section="kids">
          <h3><i class="fas fa-child"></i> Their Kids</h3>
          <i class="fas fa-chevron-down section-toggle"></i>
        </div>
        <div class="form-section-content" id="section-kids">

          <!-- Kids Primary Radio -->
          <div class="input-group">
            <div class="label-with-indicator">
              <span class="group-label">Do they have kids?</span>
              <span class="save-indicator" id="indicator-kids-filter">Saved <i class="fas fa-check"></i></span>
            </div>
            <div class="radio-group" id="kids-primary">
              <label class="radio-item">
                <input
                  type="radio"
                  name="kids-primary"
                  value="must_not_have"
                  ${filters.has_kids === 'must_not_have' ? 'checked' : ''}
                />
                <span class="radio-circle"></span>
                No
              </label>
              <label class="radio-item">
                <input
                  type="radio"
                  name="kids-primary"
                  value="must_have"
                  ${filters.has_kids === 'must_have' ? 'checked' : ''}
                />
                <span class="radio-circle"></span>
                Yes
              </label>
            </div>
          </div>

          <!-- Secondary filters (shown when "Has kids" is checked) -->
          <div id="kids-secondary-filters" style="${filters.has_kids === 'must_have' ? '' : 'display: none;'}">
            <!-- Youngest child age -->
            <div class="input-group">
              <div class="label-with-indicator">
                <span class="group-label">Youngest child age</span>
                <span class="save-indicator" id="indicator-kids-age">Saved <i class="fas fa-check"></i></span>
              </div>
              <div class="checkbox-group" id="kids_youngest_ages">
                ${KIDS_YOUNGEST_AGE_OPTIONS.map(opt => `
                  <label class="checkbox-item">
                    <input
                      type="checkbox"
                      name="kids_youngest_ages"
                      value="${opt.value}"
                      ${filters.kids_youngest_ages.includes(opt.value) ? 'checked' : ''}
                      data-filter-field="kids_youngest_ages"
                    />
                    ${opt.label}
                  </label>
                `).join('')}
              </div>
            </div>

            <!-- Number of kids -->
            <div class="input-group">
              <div class="label-with-indicator">
                <span class="group-label">Number of kids</span>
                <span class="save-indicator" id="indicator-kids-count">Saved <i class="fas fa-check"></i></span>
              </div>
              <div class="checkbox-group" id="kids_counts">
                ${KIDS_COUNT_OPTIONS.map(opt => `
                  <label class="checkbox-item">
                    <input
                      type="checkbox"
                      name="kids_counts"
                      value="${opt.value}"
                      ${filters.kids_counts.includes(parseInt(opt.value)) ? 'checked' : ''}
                      data-filter-field="kids_counts"
                    />
                    ${opt.label}
                  </label>
                `).join('')}
              </div>
            </div>

            <!-- Kids at home -->
            <div class="input-group">
              <div class="label-with-indicator">
                <span class="group-label">Do any kids live at home?</span>
                <span class="save-indicator" id="indicator-kids-at-home">Saved <i class="fas fa-check"></i></span>
              </div>
              <div class="radio-group" id="kids_at_home">
                <label class="radio-item">
                  <input
                    type="radio"
                    name="kids_at_home"
                    value="yes"
                    ${filters.kids_at_home === 'yes' ? 'checked' : ''}
                    data-filter-field="kids_at_home"
                  />
                  <span class="radio-circle"></span>
                  Yes
                </label>
                <label class="radio-item">
                  <input
                    type="radio"
                    name="kids_at_home"
                    value="no"
                    ${filters.kids_at_home === 'no' ? 'checked' : ''}
                    data-filter-field="kids_at_home"
                  />
                  <span class="radio-circle"></span>
                  No
                </label>
              </div>
            </div>
          </div>

          <!-- Wants More Kids -->
          ${renderCheckboxGroup('wants_more_kids', WANTS_MORE_KIDS_OPTIONS.filter(o => o.value), filters.wants_more_kids, wantsKidsLabel(filters.has_kids === 'must_have', 'filter'))}

        </div>
      </div>

      <!-- LIFESTYLE -->
      <div class="form-section">
        <div class="form-section-header" data-section="lifestyle">
          <h3><i class="fas fa-wine-glass"></i> Their Lifestyle</h3>
          <i class="fas fa-chevron-down section-toggle"></i>
        </div>
        <div class="form-section-content" id="section-lifestyle">

          <!-- Smoking -->
          ${renderCheckboxGroup('smoking', FREQUENCY_OPTIONS, filters.smoking, 'Their Smoking')}

          <!-- Drinking -->
          ${renderCheckboxGroup('drinking', FREQUENCY_OPTIONS, filters.drinking, 'Their Drinking')}

          <!-- Cannabis -->
          ${renderCheckboxGroup('cannabis', FREQUENCY_OPTIONS, filters.cannabis, 'Their Cannabis')}

          <!-- Drugs -->
          ${renderCheckboxGroup('drugs', FREQUENCY_OPTIONS, filters.drugs, 'Their Drug Use')}

          <!-- Exercise -->
          ${renderCheckboxGroup('exercise_levels', EXERCISE_OPTIONS, filters.exercise_levels, 'Their Exercise')}

          <!-- Relationship Structure -->
          ${renderRadioGroup('preferred_structure', PREFERRED_STRUCTURE_OPTIONS.filter(o => o.value), filters.preferred_structure, 'Their Relationship Structure')}

          <!-- Open to Long Distance -->
          ${renderCheckboxGroup('open_to_long_distance', FLEXIBILITY_OPTIONS_UI, filters.open_to_long_distance, 'Open to Long Distance?')}

          <!-- Open to Relocation -->
          ${renderCheckboxGroup('open_to_relocation', FLEXIBILITY_OPTIONS_UI, filters.open_to_relocation, 'Open to Relocation?')}

        </div>
      </div>

      <!-- BACKGROUND -->
      <div class="form-section">
        <div class="form-section-header" data-section="background">
          <h3><i class="fas fa-graduation-cap"></i> Their Background</h3>
          <i class="fas fa-chevron-down section-toggle"></i>
        </div>
        <div class="form-section-content" id="section-background">

          <!-- Education -->
          ${renderCheckboxGroup('education_levels', EDUCATION_LEVEL_OPTIONS, filters.education_levels, 'Their Education')}

          <!-- Employment -->
          ${renderCheckboxGroup('employment_statuses', EMPLOYMENT_STATUS_OPTIONS, filters.employment_statuses, 'Their Employment')}

          <!-- Languages -->
          ${renderCheckboxGroup('languages', LANGUAGES_OPTIONS, filters.languages, 'Languages They Speak')}

          <!-- Religion -->
          ${renderCheckboxGroup('religions', RELIGION_OPTIONS.filter(o => o.value), filters.religions, 'Their Religion')}

          <!-- Pets OK With -->
          ${renderCheckboxGroup('pets_ok_with', PET_TYPE_OPTIONS.filter(o => o.value !== 'none'), filters.pets_ok_with, 'Their Pets')}

        </div>
      </div>

      <!-- DEMOGRAPHICS -->
      <div class="form-section">
        <div class="form-section-header" data-section="demographics">
          <h3><i class="fas fa-users"></i> Their Demographics</h3>
          <i class="fas fa-chevron-down section-toggle"></i>
        </div>
        <div class="form-section-content" id="section-demographics">

          <!-- Ethnicity -->
          ${renderCheckboxGroup('ethnicities', ETHNICITY_OPTIONS, filters.ethnicities, 'Their Ethnicity')}

          <!-- Sexual Orientation -->
          ${renderCheckboxGroup('sexual_orientations', SEXUAL_ORIENTATION_OPTIONS, filters.sexual_orientations, 'Their Sexual Orientation')}

          <!-- Political Views -->
          ${renderCheckboxGroup('political_views', POLITICAL_VIEW_OPTIONS, filters.political_views, 'Their Political Views')}

          <!-- Income Range -->
          ${renderCheckboxGroup('income_ranges', INCOME_RANGE_OPTIONS, filters.income_ranges, 'Their Income')}

          <!-- Star Sign -->
          ${renderCheckboxGroup('zodiac_signs', ZODIAC_OPTIONS, filters.zodiac_signs, 'Their Star Sign')}

        </div>
      </div>

      <!-- INTIMACY (gated) -->
      ${showSexualFilters ? `
      <div class="form-section">
        <div class="form-section-header" data-section="intimacy">
          <h3><i class="fas fa-check"></i> Their Intimacy</h3>
          <i class="fas fa-chevron-down section-toggle"></i>
        </div>
        <div class="form-section-content" id="section-intimacy">

          <!-- Sexual Role -->
          ${renderCheckboxGroup('sexual_roles', SEXUAL_ROLE_OPTIONS, filters.sexual_roles, 'Their Role')}

          <!-- Sexual Dynamics -->
          ${renderCheckboxGroup('sexual_dynamics', SEXUAL_DYNAMICS_OPTIONS, filters.sexual_dynamics, 'Their Dynamics')}

        </div>
      </div>
      ` : ''}

      <!-- WHO THEY ARE OPEN TO -->
      <div class="form-section">
        <div class="form-section-header" data-section="disclosures">
          <h3><i class="fas fa-rainbow"></i> Who They Are Open To</h3>
          <i class="fas fa-chevron-down section-toggle"></i>
        </div>
        <div class="form-section-content" id="section-disclosures">
          <div class="input-hint mb-3">Only show people who have disclosed these preferences</div>

          <!-- Inclusive of Trans & Non-binary -->
          <div class="input-group">
            <label class="checkbox-item">
              <input
                type="checkbox"
                id="inclusive_of_trans_nb"
                ${filters.inclusive_of_trans_nb ? 'checked' : ''}
              />
              Inclusive of trans & non-binary people
            </label>
            <span class="save-indicator" id="indicator-inclusive_of_trans_nb">Saved <i class="fas fa-check"></i></span>
          </div>

          <!-- Open to ENM/Poly -->
          <div class="input-group">
            <label class="checkbox-item">
              <input
                type="checkbox"
                id="open_to_enm_poly"
                ${filters.open_to_enm_poly ? 'checked' : ''}
              />
              Open to ENM / polyamory
            </label>
            <span class="save-indicator" id="indicator-open_to_enm_poly">Saved <i class="fas fa-check"></i></span>
          </div>

        </div>
      </div>

    </div>
  `;

  // === ACTIVITY TAB ===
  activityTab.innerHTML = `
    <div class="profile-edit-form">
      <div class="form-section">
        <div class="form-section-header" data-section="activity-filters">
          <h3><i class="fas fa-circle-check"></i> Activity Filters <span class="save-indicator" id="indicator-activity-filters">Saved <i class="fas fa-check"></i></span></h3>
          <i class="fas fa-chevron-down section-toggle"></i>
        </div>
        <div class="form-section-content" id="section-activity-filters">
          <div class="input-group">
            <label class="checkbox-item">
              <input type="checkbox" id="activity-liked-me" ${isLikesMode || filters.activity_liked_me ? 'checked' : ''} ${isLikesMode ? 'disabled' : ''} />
              They're interested${isLikesMode ? ' (always on for Likes page)' : ''}
            </label>
          </div>
          <div class="input-group">
            <label class="checkbox-item">
              <input type="checkbox" id="activity-i-liked" ${filters.activity_i_liked ? 'checked' : ''} />
              I sent interest
            </label>
          </div>
          <div class="input-group">
            <label class="checkbox-item">
              <input type="checkbox" id="activity-i-disliked" ${filters.activity_i_disliked ? 'checked' : ''} />
              I passed
            </label>
          </div>
        </div>
      </div>
    </div>
  `;
}
